import { ClickableText } from '@bitstopco/bitstop-theme';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

export interface NormalizedBulkCreateResult {
  atm_id?: number;
  location_id?: number;
  success: boolean;
  entity_id?: string;
  display_id?: number;
  error?: string;
}

interface BulkCreateResultsConfig {
  // e.g. "case" / "service ticket" - used in the summary sentence.
  entityLabel: string;
  // e.g. "Case" / "Ticket" - table column header.
  columnLabel: string;
  // e.g. "Case" / "Service Ticket" - ClickableText accessible name.
  clickableName: string;
  createMoreLabel: string;
  backLabel: string;
  detailPath: (entityId: string) => string;
}

interface BulkCreateResultsProps extends BulkCreateResultsConfig {
  results: NormalizedBulkCreateResult[];
  onDone: () => void;
  onCreateMore: () => void;
}

export const BulkCreateResults = ({
  results,
  entityLabel,
  columnLabel,
  clickableName,
  createMoreLabel,
  backLabel,
  detailPath,
  onDone,
  onCreateMore,
}: BulkCreateResultsProps) => {
  const successCount = results.filter((result) => result.success).length;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Bulk Creation Results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {successCount} of {results.length} {entityLabel}(s) created successfully.
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Target</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>{columnLabel}</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((result, index) => {
              const targetLabel =
                result.atm_id !== undefined && result.atm_id !== null
                  ? `Machine ${result.atm_id}`
                  : `Location ${result.location_id}`;
              return (
                <TableRow key={`${targetLabel}-${index}`}>
                  <TableCell>{targetLabel}</TableCell>
                  <TableCell>
                    <Box
                      component="span"
                      sx={{
                        color: result.success ? 'success.main' : 'error.main',
                        fontWeight: 600,
                      }}
                    >
                      {result.success ? 'Success' : 'Failed'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {result.entity_id ? (
                      <ClickableText
                        fixIcon
                        text={result.display_id ? `#${result.display_id}` : result.entity_id}
                        clipboardValue={result.entity_id}
                        name={clickableName}
                        to={`${window.location.origin}${detailPath(result.entity_id)}`}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{result.error || '-'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button variant="outlined" onClick={onCreateMore}>
          {createMoreLabel}
        </Button>
        <Button variant="contained" onClick={onDone}>
          {backLabel}
        </Button>
      </Box>
    </Box>
  );
};
